import type { Material, Mesh, Object3D, Scene } from 'three';
import type { Transition } from './transition';
import type { StateContext } from '../state/types';
import { getStateTag, type StateTag } from '../scene/tagging';

// Sample alternative transition — NOT a chosen UX, just one possibility.
// Notes for production:
//  - Clones materials per mesh, which breaks material/InstancedMesh sharing.
//    Real assets should use a smarter approach (e.g. shader uniform or
//    per-instance attribute).
//  - 'both'-tagged nodes stay fully opaque through the transition.
//
// Cache model: we collect every **outermost** Object3D that carries a
// state tag — i.e., a stated node whose ancestors don't also carry state.
// Each such node gathers all its mesh descendants and the crossfade
// applies opacity to those meshes as a group. Hero roots tagged via
// heroLoader fit this naturally: the root has a state, its children
// don't, and toggling the root's tag flips visibility for the whole
// subtree. Plain scene meshes tagged directly in Blender still work
// because they're their own "outermost stated" node.
//
// Hygiene (PF3):
//  - init() is idempotent. The dev HEROES-tab retag re-init()s us to re-cache
//    after a hero's tag flips; without teardown each pass would clone fresh
//    materials over the previous clones and leak the old ones. So we save each
//    mesh's ORIGINAL material (post-forceOpaqueMaterials, the state we want to
//    return to — NOT the raw GLB) and restore + dispose the clone before
//    re-cloning. Material count stays flat across repeated retags.
//  - update() early-returns at steady state. Off a transition the opacities
//    never change, so re-writing ~16 meshes' opacity + visible every frame is
//    pure churn — write once per era settle, then skip until the next morph.
//  - dispose() restores the original materials (disposing the clones) and
//    leaves visibility as the last update() set it — era-correct. The old
//    dispose forced visible:true, which popped hidden-era heroes back on.
// The clone's `transparent` flag is set once at init and never flipped at a
// morph boundary, so the GL program cache key is stable (no per-morph compile).
interface StatedNode {
  root: Object3D;
  meshes: Mesh[];
}

const ORIG_KEY = '__xfadeOrig';

export class OpacityCrossfade implements Transition {
  private nodes: StatedNode[] = [];
  // The era the meshes are currently settled to (steady state). null while a
  // transition is running; undefined right after init (forces one settle pass).
  private settledEra: StateTag | null | undefined = undefined;

  init(scene: Scene): void {
    // Idempotent — a dev retag re-inits us. Restore + dispose any existing
    // clones first so repeated init()s don't pile up leaked material objects.
    this.teardown();
    scene.traverse((obj) => {
      const tag = getStateTag(obj);
      if (!tag) return;
      // Skip if an ancestor is already a stated node — we only manage
      // the outermost stated object so a hero root + tagged inner mesh
      // don't both fight for control.
      let anc = obj.parent;
      while (anc && anc !== scene) {
        const ancTag = getStateTag(anc);
        if (ancTag && ancTag !== 'both') return;
        anc = anc.parent;
      }
      // 'both' nodes are always-on and not opacity-managed — but init() must
      // (re)assert their visibility (B1). A hero hidden under a past/present tag
      // then retagged to 'both' re-init()s us; it's no longer in this.nodes, so
      // update() never touches it again, and teardown() restored its material but
      // deliberately left visible=false — so without this it stays invisible
      // forever. Scope to the 'both' subtree so unrelated hidden objects aren't
      // popped on. (Heroes are force-opaqued, so opacity 1 is their correct state.)
      if (tag === 'both') {
        obj.traverse((child) => {
          const m = child as Mesh;
          if (!m.isMesh) return;
          m.visible = true;
          this.setOpacity(m, 1);
        });
        return;
      }
      // Gather mesh descendants (including obj itself if it's a mesh).
      const meshes: Mesh[] = [];
      obj.traverse((child) => {
        const m = child as Mesh;
        if (m.isMesh) meshes.push(m);
      });
      if (meshes.length === 0) return;
      // Clone materials so opacity edits don't leak across shared materials,
      // saving the original first so teardown/dispose can restore + free the clone.
      for (const mesh of meshes) {
        mesh.userData[ORIG_KEY] = mesh.material;
        mesh.material = Array.isArray(mesh.material)
          ? mesh.material.map((m) => m.clone())
          : (mesh.material as Material).clone();
        this.setTransparent(mesh, true);
      }
      this.nodes.push({ root: obj, meshes });
    });
    // Force a settle pass on the next update (era unknown until then).
    this.settledEra = undefined;
  }

  update(ctx: StateContext): void {
    const transitioning = ctx.current !== ctx.target;
    if (!transitioning) {
      // Steady state — opacities are constant, so write them once when the
      // settled era changes (or after init), then skip every idle frame.
      if (this.settledEra === ctx.current) return;
      this.applyOpacities(ctx, false);
      this.settledEra = ctx.current;
      return;
    }
    this.applyOpacities(ctx, true);
    this.settledEra = null; // mid-transition: not settled
  }

  private applyOpacities(ctx: StateContext, transitioning: boolean): void {
    for (const node of this.nodes) {
      const tag = getStateTag(node.root) as StateTag | undefined;
      // 'both' is filtered at init time; only past/present nodes reach here.
      let op = 0;
      if (transitioning) {
        if (tag === ctx.target) op = ctx.progress;
        else if (tag === ctx.current) op = 1 - ctx.progress;
      } else if (tag === ctx.current) {
        op = 1;
      }
      const visible = op > 0.001;
      for (const mesh of node.meshes) {
        this.setOpacity(mesh, op);
        mesh.visible = visible;
      }
    }
  }

  dispose(): void {
    // Restore each mesh to its original material (freeing the clone). Leave
    // visibility as the last update() left it — era-correct; don't pop
    // hidden-era heroes back on by forcing visible:true.
    this.teardown();
    this.settledEra = undefined;
  }

  /** Restore every managed mesh's original material + dispose its clone, then
   *  drop the node list. Safe to call on an empty node list. */
  private teardown(): void {
    for (const node of this.nodes) {
      for (const mesh of node.meshes) this.restoreMesh(mesh);
    }
    this.nodes = [];
  }

  private restoreMesh(mesh: Mesh): void {
    const orig = mesh.userData[ORIG_KEY] as Material | Material[] | undefined;
    if (orig === undefined) return;
    // Dispose the clone(s) currently mounted (frees the GL program), restore
    // the saved original.
    const cur = mesh.material;
    if (Array.isArray(cur)) cur.forEach((m) => m.dispose());
    else (cur as Material).dispose();
    mesh.material = orig;
    delete mesh.userData[ORIG_KEY];
  }

  private setTransparent(mesh: Mesh, transparent: boolean) {
    const apply = (m: Material) => {
      m.transparent = transparent;
      m.needsUpdate = true;
    };
    if (Array.isArray(mesh.material)) mesh.material.forEach(apply);
    else apply(mesh.material as Material);
  }

  private setOpacity(mesh: Mesh, op: number) {
    const apply = (m: Material) => {
      (m as Material & { opacity: number }).opacity = op;
    };
    if (Array.isArray(mesh.material)) mesh.material.forEach(apply);
    else apply(mesh.material as Material);
  }
}
